import React, { useEffect, useMemo, useState } from 'react'
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import Checkbox from '../../../../components/forms/Checkbox'
import { push } from '@lagunovsky/redux-react-router'
import ManagementUnitAction from '../actions/ManagementUnitAction'
import SieauAction from '../../../../components/sieau/SieauAction'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import { isNil, lowerCase, orderBy } from 'lodash'
import { getDate } from '../../../../utils/DateUtil'
import { exportFile } from '../../../../utils/ExportDataUtil'
import DtoManagementUnit from '../dto/DtoManagementUnit'
import TableManagementUnitVolumes from './TableManagementUnitVolumes'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import { hasValue } from 'utils/NumberUtil'
import TableManagementUnitStations from './TableManagementUnitStations'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import DtoPiezometerLight from 'piezometry/dto/DtoPiezometerLight'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'
import SimpleSelect from 'components/forms/SimpleSelect'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { formatMilliers } from 'utils/StringUtil'


const FormManagementUnit = ({ managementUnit, setManagementUnit, editMode }) => (
    <Grid container spacing={1}>
        <Grid size={6}>
            <SimpleInput
                title={ i18n.id }
                value={ managementUnit.id }
                onChange={ (v) => setManagementUnit(prev => ({ ...prev, id: v })) }
                maxLength={ 5 }
                disabled={!editMode}
            />
        </Grid>
        <Grid size={6}>
            <SimpleDatePicker
                label={ i18n.lowWaterStartDate }
                value={ managementUnit.lowWaterStartDate }
                onChange={v => setManagementUnit(prev => ({ ...prev, lowWaterStartDate: v }))}
                max={ managementUnit.lowWaterEndDate }
                disabled={!editMode}
                views={['month', 'day']}
                openTo='month'
                format='DD/MM'
                slotProps={{
                    textField: { fullWidth: true, placeholder: 'DD/MM' },
                    calendarHeader: { format: 'MMMM' },
                    toolbar: { hidden: true },
                }}
            />
        </Grid>
        <Grid size={6}>
            <SimpleInput
                title={ i18n.name }
                value={ managementUnit.name }
                onChange={ (v) => setManagementUnit(prev => ({ ...prev, name: v })) }
                maxLength={ 255 }
                disabled={!editMode}
            />
        </Grid>
        <Grid size={6}>
            <SimpleDatePicker
                label={ i18n.lowWaterEndDate }
                value={ managementUnit.lowWaterEndDate }
                onChange={v => setManagementUnit(prev => ({ ...prev, lowWaterEndDate: v }))}
                min={ managementUnit.lowWaterStartDate }
                disabled={!editMode}
                views={['month', 'day']}
                openTo='month'
                format='DD/MM'
                slotProps={{
                    textField: { fullWidth: true, placeholder: 'DD/MM' },
                    calendarHeader: { format: 'MMMM' },
                    toolbar: { hidden: true },
                }}
            />
        </Grid>
        <Grid size={6}>
            <Checkbox
                label={ i18n.annualBreakdown }
                checked={ managementUnit.annualBreakdown }
                onChange={ (v) => setManagementUnit(prev => ({ ...prev, annualBreakdown: v })) }
                disabled={!editMode}
            />
        </Grid>
        <Grid size={6}>
            <Checkbox
                label={ i18n.lowWaterVentilation }
                checked={ managementUnit.lowWaterVentilation }
                onChange={ (v) => setManagementUnit(prev => ({ ...prev, lowWaterVentilation: v })) }
                disabled={!editMode}
            />
        </Grid>
        <Grid size={6}>
            <Checkbox
                label={ i18n.monthlyBreakdown }
                checked={ managementUnit.monthlyBreakdown }
                onChange={ (v) => setManagementUnit(prev => ({ ...prev, monthlyBreakdown: v })) }
                disabled={!editMode}
            />
        </Grid>
        <Grid size={6}>
            <Checkbox
                label={ i18n.lowWaterOutVentilation }
                checked={ managementUnit.lowWaterOutVentilation }
                onChange={ (v) => setManagementUnit(prev => ({ ...prev, lowWaterOutVentilation: v })) }
                disabled={!editMode}
            />
        </Grid>
    </Grid>
)

FormManagementUnit.propTypes = {
    managementUnit: PropTypes.instanceOf(DtoManagementUnit),
    setManagementUnit: PropTypes.func,
    editMode: PropTypes.bool,
}

const VP_LEVEL_FIELDS = [
    { key: 'restrictedVPLevelValue', title: 'VPRestricted' },
    { key: 'normalVPLevelValue', title: 'VPNormal' },
    { key: 'highVPLevelValue', title: 'VPHigh' },
    { key: 'veryHighVPLevelValue', title: 'VPVeryHigh' },
]

const VPLevelValueInput = ({ managementUnit, setManagementUnit, editMode, fieldKey, title }) => {
    const globalValue = useApplicationSetting(fieldKey, v => hasValue(v) ? intParser(v) : null)
    const globalValueLabel = isNil(globalValue) ? lowerCase(i18n.notDefined) : `${formatMilliers(globalValue)} m3`
    return (
        <SimpleInput
            title={i18n[title]}
            type={INPUT_TYPES.int}
            value={managementUnit[fieldKey] ?? ''}
            onChange={v => setManagementUnit(prev => ({ ...prev, [fieldKey]: hasValue(v) ? parseInt(v) : null }))}
            helperText={`${i18n.globalSetting} : ${globalValueLabel}`}
            disabled={!editMode}
        />
    )
}

VPLevelValueInput.propTypes = {
    managementUnit: PropTypes.instanceOf(DtoManagementUnit),
    setManagementUnit: PropTypes.func,
    editMode: PropTypes.bool,
    fieldKey: PropTypes.string,
    title: PropTypes.string,
}

const ManagementUnitApp = ({}) => {
    const {
        managementUnit,
        managementUnits,
        hydrometricStations,
        pluviometers,
        piezometersLight,
    } = useSelector(store => ({
        managementUnit: store.ManagementUnitReducer.managementUnit,
        managementUnits: store.ManagementUnitReducer.managementUnits,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        pluviometers: store.PluviometryReducer.pluviometers,
        piezometersLight: store.PiezometryReducer.piezometersLight,
    }), shallowEqual)

    const dispatch = useDispatch()
    const { code } = useParams()

    const [managementUnitToUpdate, setManagementUnitToUpdate] = useState(managementUnit)
    const [subManagementUnitToUpdate, setSubManagementUnitToUpdate] = useState({})
    const [openDialog, setOpenDialog] = useState(false)
    const [isCreate, setIsCreate] = useState(false)
    const [editMode, setEditMode] = useState(code === 'new')

    useEffect(() => {
        if (code !== 'new') {
            dispatch(ManagementUnitAction.fetchManagementUnit(code)).then(() => {
                dispatch(ManagementUnitAction.fetchManagementUnits())
            })
        }
        if (!hydrometricStations.length) {
            dispatch(HydrometryAction.fetchHydrometricStations())
        }
        if (!pluviometers.length) {
            dispatch(PluviometryAction.fetchPluviometers())
        }
        if (!piezometersLight.length) {
            dispatch(PiezometryAction.fetchPiezometersLight())
        }
    }, [code])

    useEffect(() => {
        setManagementUnitToUpdate(managementUnit)
    }, [managementUnit])

    useTitle(() => ([
        {
            title: i18n.referencials,
            href: '/referencial',
        },
        {
            title: i18n.managementUnit,
            href: '/referencial/managementUnit/',
        },
        {
            title: code !== 'new' ? managementUnit.name : i18n.newManagementUnit,
            href: `/referencial/managementUnit/${code}`,
        },
    ]), [code, managementUnit])

    const valideUpdate = () => {
        dispatch(ManagementUnitAction.updateManagementUnit(managementUnitToUpdate)).then(() => {
            dispatch(ManagementUnitAction.fetchManagementUnit(managementUnit.managementCode)).then(() => {
                setOpenDialog(false)
            })
            dispatch(ManagementUnitAction.fetchManagementUnits())
        })
    }

    const getFormattedObject = (managementUnitToFormat) => {
        return ({
            ...managementUnitToFormat,
            parent: managementUnit.managementCode === managementUnitToFormat.managementCode ? null : managementUnit.managementCode,
        })
    }

    const valideUpdateSubUnit = () => {
        const formattedManagementUnitToUpdate = getFormattedObject(subManagementUnitToUpdate)
        dispatch(ManagementUnitAction.updateManagementUnit(formattedManagementUnitToUpdate)).then(() => {
            dispatch(ManagementUnitAction.fetchManagementUnit(managementUnit.managementCode)).then(() => {
                setOpenDialog(false)
                setSubManagementUnitToUpdate({})
            })
            dispatch(ManagementUnitAction.fetchManagementUnits())
        })
    }

    const valideCreate = (mu) => {
        const formattedManagementUnitToUpdate = getFormattedObject(mu)
        dispatch(ManagementUnitAction.createManagementUnit(formattedManagementUnitToUpdate)).then((newId) => {
            dispatch(ManagementUnitAction.fetchManagementUnits()).then(() => {
                if (code === 'new') {
                    dispatch(ManagementUnitAction.fetchManagementUnit(newId)).then(() => {
                        dispatch(push(`/referencial/managementUnit/${newId}`))
                    })
                } else setOpenDialog(false)
            })
        })
    }

    useActions(() => (
        editMode ? {
            save: () => {
                if (code === 'new') valideCreate(managementUnitToUpdate)
                else valideUpdate()
                setEditMode(false)
            },
            cancel: () => {
                if (code !== 'new') setEditMode(false)
                else dispatch(push('/referencial/managementUnit'))
            },
        } : {
            edit: () => setEditMode(true),
        }
    ), [code, editMode, managementUnitToUpdate])

    const subManagementunitsDatas = useMemo(() => orderBy(
        managementUnits.filter((mu) => mu.managementCode !== managementUnit.managementCode && mu.parent === managementUnit.managementCode), 'id', 'asc',
    ).map(mu => ({
        ...mu,
        lowWaterStartDateDisplay: getDate(mu.lowWaterStartDate, 'DD/MM'),
        lowWaterEndDateDisplay: getDate(mu.lowWaterEndDate, 'DD/MM'),
        hiddenCode: mu.managementCode,
        hiddenId: mu.id,
    })), [managementUnits, managementUnit])

    const onRemoveUGE = (mu) => {
        dispatch(ManagementUnitAction.updateManagementUnit({ ...mu, parent: null })).then(() => {
            dispatch(ManagementUnitAction.fetchManagementUnits())
        })
    }

    const getExportData = () => {
        return managementUnits.map((mu, i) => {
            return i === 0 ? { headers: ['id', 'name', 'lowWaterStartDate', 'lowWaterEndDate'], ...mu } : mu
        })
    }

    const addAction = [{
        icon: 'note_add',
        tooltip: i18n.add,
        onClick: () => {
            setOpenDialog(true)
            setSubManagementUnitToUpdate({})
            setIsCreate(true)
        },
        displayed: editMode,
    }]

    const lineActions =[{
        icon: 'delete',
        tooltip: i18n.delete,
        onClick: m => onRemoveUGE(m),
        displayed: editMode,
    }, {
        icon: 'edit',
        tooltip: i18n.edit,
        onClick: m => {
            setOpenDialog(true)
            setSubManagementUnitToUpdate(m)
            setIsCreate(false)
        },
        displayed: editMode,
    }]

    return (
        <Stack spacing={1}>
            <Card>
                <CardTitle title={i18n.description}/>
                <CardContent>
                    <FormManagementUnit
                        managementUnit={managementUnitToUpdate}
                        setManagementUnit={setManagementUnitToUpdate}
                        editMode={editMode}
                    />
                </CardContent>
            </Card>

            {code !== 'new' && (
                <Card>
                    <CardTitle title={i18n.withdrawableVolumesA3P}/>
                    <CardContent>
                        <Grid container spacing={1}>
                            {VP_LEVEL_FIELDS.map(({ key, title }) => (
                                <Grid size={3} key={key}>
                                    <VPLevelValueInput
                                        managementUnit={managementUnitToUpdate}
                                        setManagementUnit={setManagementUnitToUpdate}
                                        editMode={editMode}
                                        fieldKey={key}
                                        title={title}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {code !== 'new' && !hasValue(managementUnit.parent) && (
                <CardTable
                    title={i18n.listSubManagementUnits}
                    rows={subManagementunitsDatas}
                    headers={[
                        'id',
                        'name',
                        { key: 'lowWaterStartDateDisplay', value: i18n.lowWaterStartDate },
                        { key: 'lowWaterEndDateDisplay', value: i18n.lowWaterEndDate },
                    ]}
                    actions={[{
                        icon: 'file_download',
                        tooltip: i18n.export,
                        onClick: () => exportFile({
                            data: getExportData(),
                            exportType: 'xlsx',
                            titleFile: i18n.listSubManagementUnits,
                        }),
                    }, ...addAction]}
                    lineActions={lineActions}
                />
            )}
            {managementUnit.link_aup && code !== 'new' && (
                <TableManagementUnitVolumes
                    managementUnit={managementUnitToUpdate}
                    changeManagementUnit={setManagementUnitToUpdate}
                    isEdit={editMode}
                />
            )}
            {managementUnit.linkStations && code !== 'new' && (
                <TableManagementUnitStations
                    managementUnit={managementUnitToUpdate}
                    changeManagementUnit={setManagementUnitToUpdate}
                    isEdit={editMode}
                />
            )}
            <Dialog
                fullWidth
                maxWidth='md'
                open={openDialog}
            >
                <DefaultDialogTitle
                    title={isCreate ? i18n.addUnderManagementUnit : i18n.updateUnderManagementUnit}
                    onClose={() => {
                        setOpenDialog(false)
                        setIsCreate(false)
                        setSubManagementUnitToUpdate({})
                    }}
                />
                <DialogContent>
                    <Stack spacing={1}>
                        {isCreate && <SimpleSelect
                            label={i18n.managementUnit}
                            value={subManagementUnitToUpdate.managementCode}
                            options={managementUnits.filter(m => m.parent !== managementUnit.managementCode && m.managementCode !== managementUnit.managementCode)}
                            keyValue='managementCode'
                            keyName='labelWithCode'
                            onChange={v => setSubManagementUnitToUpdate(managementUnits.find(m => m.managementCode === v) || {})}
                        />}
                        <FormManagementUnit managementUnit={subManagementUnitToUpdate} setManagementUnit={setSubManagementUnitToUpdate} editMode />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => isCreate && !hasValue(managementUnitToUpdate.managementCode) ? valideCreate(subManagementUnitToUpdate) : valideUpdateSubUnit()} variant='contained' color='primary'>
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

ManagementUnitApp.propTypes = {
    params: PropTypes.shape({
        code: PropTypes.string,
    }),
    managementUnit: PropTypes.instanceOf(DtoManagementUnit),
    managementUnits: PropTypes.arrayOf(PropTypes.instanceOf(DtoManagementUnit)),
    hydrometricStations: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
    pluviometers: PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
    piezometersLight: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)),
    fetchManagementUnit: PropTypes.func,
    fetchManagementUnits: PropTypes.func,
    forceFetch: PropTypes.func,
    createManagementUnit: PropTypes.func,
    updateManagementUnit: PropTypes.func,
    fetchPiezometersLight: PropTypes.func,
    fetchPluviometers: PropTypes.func,
    fetchHydrometricStations: PropTypes.func,
}

const mapStateToProps = store => {
    return {
        managementUnit: store.ManagementUnitReducer.managementUnit,
        managementUnits: store.ManagementUnitReducer.managementUnits,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        pluviometers: store.PluviometryReducer.pluviometers,
        piezometersLight: store.PiezometryReducer.piezometersLight,
    }
}

const mapDispatchToProps = {
    forceFetch: SieauAction.forceFetch,
    fetchManagementUnit: ManagementUnitAction.fetchManagementUnit,
    fetchManagementUnits: ManagementUnitAction.fetchManagementUnits,
    createManagementUnit: ManagementUnitAction.createManagementUnit,
    updateManagementUnit: ManagementUnitAction.updateManagementUnit,
    fetchPiezometersLight: PiezometryAction.fetchPiezometersLight,
    fetchPluviometers: PluviometryAction.fetchPluviometers,
    fetchHydrometricStations: HydrometryAction.fetchHydrometricStations,
    push,
}

export default connect(mapStateToProps, mapDispatchToProps)(ManagementUnitApp)
